import api from './api';

export interface DashboardData {
  totalExpenses: number;
  totalIncome: number;
  totalGifts: number;
  monthlyExpenses: number;
  monthlyIncome: number;
  monthlyGifts: number;
  topExpenseCategories: Array<{
    name: string;
    amount: number;
    percentage: number;
  }>;
  recentTransactions: Array<{
    type: 'expense' | 'income' | 'gift';
    amount: number;
    description: string;
    date: string;
  }>;
}

export interface IncomeHistoryTransaction {
  id: number;
  amount: number;
  transaction_type: string;
  income_date: string;
  notes?: string;
  created_at: string;
  source_name: string;
  wallet_name: string;
}

export interface IncomeHistoryResponse {
  transactions: IncomeHistoryTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface IncomeByGroup {
  group_name: string;
  amount: number;
  percentage: number;
  icon?: string;
  color?: string;
  transaction_count?: number;
}

export interface IncomeByGroupResponse {
  incomeByGroup: IncomeByGroup[];
}

export interface DashboardResponse {
  success: boolean;
  data: DashboardData;
}

// Thêm interface cho performance monitoring
export interface DashboardPerformanceMetrics {
  queryTime: number;
  dataSize: number;
  cacheHit: boolean;
}

class DashboardService {
  // Cache để tối ưu performance
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 phút

  private getCacheKey(endpoint: string, params: any): string {
    return `${endpoint}:${JSON.stringify(params)}`;
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  async getDashboardData(params?: {
    start_date?: string;
    end_date?: string;
    month?: string; // Format: YYYY-MM
  }): Promise<DashboardData> {
    try {
      const cacheKey = this.getCacheKey('dashboard-summary', params);
      const cached = this.cache.get(cacheKey);
      
      if (cached && this.isCacheValid(cached.timestamp)) {
        console.log('📊 Dashboard data loaded from cache');
        return cached.data;
      }

      // Gọi API dashboard summary mới với tham số tháng
      const queryParams = new URLSearchParams();
      if (params?.month) {
        queryParams.append('month', params.month);
      }
      
      const startTime = Date.now();
      const response = await api.get(`/api/dashboard/summary?${queryParams.toString()}`);
      const queryTime = Date.now() - startTime;
      
      console.log(`📊 Dashboard data loaded in ${queryTime}ms`);
      
      // Cache kết quả
      this.cache.set(cacheKey, {
        data: response.data.data,
        timestamp: Date.now()
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  async getIncomeHistory(params?: {
    page?: number;
    limit?: number;
    month?: string; // Format: YYYY-MM
  }): Promise<IncomeHistoryResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.month) {
        queryParams.append('month', params.month);
      }
      
      const response = await api.get(`/api/dashboard/income-history?${queryParams.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching income history:', error);
      throw error;
    }
  }

  async getIncomeByGroup(params?: {
    month?: string; // Format: YYYY-MM
  }): Promise<IncomeByGroupResponse> {
    try {
      const cacheKey = this.getCacheKey('income-by-group', params);
      const cached = this.cache.get(cacheKey);
      
      if (cached && this.isCacheValid(cached.timestamp)) {
        console.log('💰 Income by group data loaded from cache');
        return cached.data;
      }

      const queryParams = new URLSearchParams();
      if (params?.month) {
        queryParams.append('month', params.month);
      }
      
      const startTime = Date.now();
      const response = await api.get(`/api/dashboard/income-by-group?${queryParams.toString()}`);
      const queryTime = Date.now() - startTime;
      
      console.log(`💰 Income by group data loaded in ${queryTime}ms`);
      
      // Cache kết quả
      this.cache.set(cacheKey, {
        data: response.data.data,
        timestamp: Date.now()
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching income by group:', error);
      throw error;
    }
  }

  // Method để clear cache khi cần
  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ Dashboard cache cleared');
  }

  // Method để lấy thông tin cache
  getCacheInfo(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

export const dashboardService = new DashboardService(); 